import { DOCUMENT } from '@angular/common';
import { DestroyRef, Inject, Injectable, InjectionToken, Renderer2, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HammerGestureConfig } from '@angular/platform-browser';

import { AppConfig } from '@frontend/sports/common/client-config-data-access';
import { MutationObserverService } from '@frontend/sports/common/core/utils/dom';
import { fromSportsLeave } from '@frontend/sports/host-app/sports-product/feature/utils';
import fastdom from 'fastdom';
import { TouchMouseInput } from 'hammerjs';
import { Subscription, animationFrames, delay, exhaustMap, filter, fromEvent, merge, take, tap } from 'rxjs';

import { GestureOptions, PopupAction } from '../common/modal-window.component';
import { ModalAnimationHelperService } from './modal-animation-helper.service';
import { OpenedModalsStorageService } from './opened-modals-storage.service';

export const VERTICAL_GESTURE_CONFIG = new InjectionToken<HammerGestureConfig>('ModalGestureVerticalConfig');

@Injectable()
export class ModalGestureVerticalConfig extends HammerGestureConfig {
    override overrides = {
        pan: {
            direction: Hammer.DIRECTION_VERTICAL,
        },
    };

    override options = {
        inputClass: TouchMouseInput,
        touchAction: 'pan-y',
    };
}

@Injectable()
export class ModalGestureService {
    private readonly willChange = 'will-change';
    private isDragging = false;
    private isAnimating = false;
    private currentY = 0;
    private readonly destroyRef = inject(DestroyRef);
    private currentBackgroundScale = this.animationHelper.minBackgroundScaleValue;
    private currentBackdropOpacity = this.animationHelper.maxBackdropOpacity;
    private modalWindow: HTMLElement;
    private backgroundWindow: HTMLElement | null;
    private readonly closingVelocity = 0.5; //velocity expressed in px/ms.
    private readonly coverElementHeight = window.screen.height * 0.05;
    private isEdpModalBackground: boolean;
    private readonly sportsLeaveSub: Subscription;
    private readonly _doc = inject(DOCUMENT);
    private _scrollContainer: HTMLElement | null;
    private _headerHeight?: number;
    private scrollTargetQuerySelector?: string;
    private headerSelector?: string;

    private get modalBackdrop(): HTMLElement {
        const backdrops = this._doc.querySelectorAll<HTMLElement>('ms-modal-backdrop');
        return backdrops[backdrops.length - 1];
    }

    private get modalCover(): HTMLElement {
        const covers = this._doc.querySelectorAll<HTMLElement>('ms-modal-cover');
        return covers[covers.length - 1];
    }

    private get edpCoverElement(): HTMLElement | null {
        return this._doc.querySelectorAll<HTMLElement>('.modal-gestures-cover')[0];
    }

    private get slotApp(): HTMLElement | null {
        return this._doc.querySelector<HTMLElement>('.slot-app');
    }

    private get scrollContainer(): HTMLElement | null {
        if (!this.scrollTargetQuerySelector) {
            return null;
        }

        return (this._scrollContainer ??= this.modalWindow.querySelector<HTMLElement>(this.scrollTargetQuerySelector));
    }

    private get headerHeight(): number {
        if (!this.headerSelector) {
            return 0;
        }

        return (this._headerHeight ??= this.modalWindow.querySelector<HTMLElement>(this.headerSelector)?.clientHeight ?? 0);
    }

    constructor(
        @Inject(VERTICAL_GESTURE_CONFIG) private hammerGestureConfig: HammerGestureConfig,
        private openedModals: OpenedModalsStorageService,
        private appConfig: AppConfig,
        private renderer: Renderer2,
        private animationHelper: ModalAnimationHelperService,
        private mutationObserverService: MutationObserverService,
    ) {
        if (this.appConfig.modalDialogGesturesEnabled) {
            this.destroyRef.onDestroy(() => {
                this.sportsLeaveSub.unsubscribe();
                this.onDestroyCleanup();
            });

            this.sportsLeaveSub = fromSportsLeave().subscribe(() => {
                this.onDestroyCleanup();
            });
        }
    }

    private onDestroyCleanup(): void {
        if (!this.openedModals.count && this.modalWindow) {
            this.renderer.removeAttribute(this.modalWindow, 'style');
            this.renderer.removeAttribute(this.backgroundWindow, 'style');
        }
    }

    addGestures(modalWindow: unknown, { enabled, headerSelector, scrollTargetQuerySelector }: GestureOptions): void {
        if (!enabled || !(modalWindow instanceof HTMLElement)) {
            return;
        }

        this.isEdpModalBackground = !!this.openedModals.edpModal;
        this.backgroundWindow = this.isEdpModalBackground
            ? this._doc.querySelector<HTMLElement>('.event-details-modal')
            : this._doc.querySelector<HTMLElement>('.app-root');

        this.modalWindow = modalWindow;
        this.scrollTargetQuerySelector = scrollTargetQuerySelector;
        this.headerSelector = headerSelector;

        if (this.headerSelector === 'bs-digital-balance-header' && this.openedModals.count >= 1) {
            this.mutationObserverService
                .observe(this.modalWindow)
                .pipe(
                    filter((ms) => ms.some((m) => Array.from(m.addedNodes).some((node) => node.nodeName.toLowerCase() === 'bs-betslip'))),
                    delay(this.animationHelper.animationDurationY),
                    takeUntilDestroyed(this.destroyRef),
                )
                .subscribe(() => {
                    this.initCoverElement(true);
                });
        } else {
            this.initCoverElement();
            this.initModalBackdrop();
        }

        this.handlePanEvents(this.modalWindow);
        this.modifyModalHeight();
    }

    private initModalBackdrop(): void {
        if (this.openedModals.count <= 1 || this.isEdpModalBackground) {
            fastdom.mutate(() => {
                this.renderer.setStyle(this.backgroundWindow, this.willChange, 'transform');
                requestAnimationFrame(() => {
                    this.animationHelper.animateBackdrop(0, this.animationHelper.maxBackdropOpacity, this.animationHelper.animationDurationY);
                    // We use the coverElement to hide content above the header.
                    this.animationHelper.animateCoverElement(
                        this.animationHelper.minCoverElementPosition,
                        this.animationHelper.maxCoverElementPosition,
                        this.animationHelper.animationDurationY,
                    );
                    this.renderer.setStyle(this.backgroundWindow, 'display', 'block');
                    this.animateBackground(this.animationHelper.minBackgroundScale, this.animationHelper.animationDurationY);
                });
            });
        }
    }

    private initCoverElement(betslipCase?: boolean): void {
        if (this.openedModals.count > 1 && !this.openedModals.edpModal && !betslipCase) {
            return;
        }

        if (this.openedModals.edpModal) {
            this.renderer.addClass(this.modalCover, 'event-details-overlay');
        } else {
            this.renderer.setStyle(this.modalCover, 'height', `${this.coverElementHeight}px`);
        }
    }

    private handlePanEvents(panElement: HTMLElement): void {
        const hammer = this.hammerGestureConfig.buildHammer(panElement);

        fromEvent<HammerInput>(hammer, 'panstart')
            .pipe(
                filter((pan) => this.allowedToPan(pan)),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(() =>
                fastdom.mutate(() => {
                    this.handlePanstart();
                }),
            );

        fromEvent<HammerInput>(hammer, 'panmove')
            .pipe(
                filter(
                    (pan) => (pan.direction === Hammer.DIRECTION_UP || pan.direction === Hammer.DIRECTION_DOWN) && this.isDragging && pan.deltaY > 0,
                ),
                takeUntilDestroyed(this.destroyRef),
                exhaustMap((pan) =>
                    animationFrames().pipe(
                        take(1),
                        filter(() => !this.isAnimating),
                        tap(() =>
                            fastdom.mutate(() => {
                                this.handlePanmove(pan);
                            }),
                        ),
                    ),
                ),
            )
            .subscribe();

        merge(
            fromEvent<HammerInput>(hammer, 'panend'),
            fromEvent(panElement, 'touchend', { capture: true }).pipe(delay(50)), // Wait if panend emits
        )
            .pipe(
                filter(() => this.isDragging),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((event) =>
                fastdom.mutate(() => {
                    this.handlePanend((event as HammerInput)?.velocity || 0);
                }),
            );
    }

    private allowedToPan(pan: HammerInput): boolean {
        const scrolledTop = !!(this.scrollContainer && this.scrollContainer.scrollTop < 10);
        const panOnHeader = this.headerHeight ? Math.round((pan.srcEvent as TouchEvent).touches[0].clientY) <= this.headerHeight : true;

        return !this.isDragging && !this.isAnimating && pan.direction === Hammer.DIRECTION_DOWN && (scrolledTop || panOnHeader);
    }

    private handlePanstart(): void {
        this.isDragging = true;
        if (this.openedModals.count <= 1) {
            this.renderer.setStyle(this.backgroundWindow, 'display', 'block');
            this.renderer.setStyle(this.backgroundWindow, this.willChange, 'transform');
            this.animationHelper.setTransformOriginCoordinates();
        }
        this.animationHelper.tryHideSlotApp();
        this.renderer.setStyle(this.modalWindow, 'height', '100%');
        this.renderer.setStyle(this.modalWindow, this.willChange, 'top');
    }

    private handlePanmove(event: HammerInput): void {
        this.currentY = event.deltaY;
        if (this.currentY <= window.screen.height) {
            this.setBackgroundScaleOnPanMove(this.currentY);
            this.setModalTop(this.currentY);
        } else {
            this.panClose();
        }
    }

    private handlePanend(velocity: number): void {
        if (!this.isDragging) {
            return;
        }

        this.isDragging = false;
        this.isAnimating = true;

        let animationTime = this.animationHelper.calculateAnimationTime({ currentY: this.currentY });
        const shouldClose = this.currentY > window.innerHeight * this.animationHelper.closingRatioY;

        if (velocity >= this.closingVelocity) {
            this.closeWithVelocity(shouldClose, velocity, animationTime);
        } else {
            if (shouldClose) {
                this.animateClose(animationTime);
            } else {
                this.animateModal(false, this.currentY, animationTime);
                this.animationHelper.animateBackdrop(
                    Number.parseFloat(this.modalBackdrop.style.opacity),
                    this.animationHelper.maxBackdropOpacity,
                    animationTime,
                );
                this.animationHelper.animateCoverElement(
                    this.modalCover.style.transform,
                    this.animationHelper.maxCoverElementPosition,
                    animationTime,
                );
                this.animateBackground(this.animationHelper.minBackgroundScale, animationTime, this.backgroundWindow?.style.transform);
            }
        }
    }

    private closeWithVelocity(shouldClose: boolean, velocity: number, animationTime: number): void {
        const velocityAnimationTime = (window.innerHeight - this.currentY) / velocity;

        if (!shouldClose || (velocityAnimationTime > 0 && velocityAnimationTime < animationTime)) {
            this.animateClose(velocityAnimationTime);
        } else {
            this.animateClose(animationTime);
        }
    }

    private animateClose(animationTime: number): void {
        this.setModalTop(window.innerHeight);
        this.animateModal(true, this.currentY, animationTime);
        this.animationHelper.animateBackdrop(Number.parseFloat(this.modalBackdrop.style.opacity), 0, animationTime);
        this.animationHelper.animateCoverElement(this.modalCover.style.transform, this.animationHelper.minCoverElementPosition, animationTime);
        this.animateBackground(this.animationHelper.maxBackgroundScale, animationTime, this.backgroundWindow?.style.transform);
    }

    private setModalTop(top = 0): void {
        if (top < 0) {
            return;
        }
        if (!this.isAnimating) {
            this.renderer.setStyle(this.modalWindow, 'top', `${top}px`);
        }
    }

    private setBackgroundScaleOnPanMove(currentY: number): void {
        if (this.openedModals.count > 1 && !this.isEdpModalBackground) {
            return;
        }
        const currentPanValue = currentY / window.screen.height;
        this.renderer.setStyle(this.modalCover, 'transform', `translateY(-${currentPanValue * 100}%)`);
        this.currentBackdropOpacity =
            this.animationHelper.maxBackdropOpacity - Number((this.animationHelper.maxBackdropOpacity * (currentY / window.screen.width)).toFixed(2));
        this.renderer.setStyle(this.modalBackdrop, 'opacity', `${this.currentBackdropOpacity}`);
        this.currentBackgroundScale = this.animationHelper.minBackgroundScaleValue + currentPanValue * 0.1;
        this.renderer.setStyle(this.backgroundWindow, 'transform', `scale(${this.currentBackgroundScale})`);
    }

    private animateBackground(toScale: string, duration: number, fromScale = this.animationHelper.maxBackgroundScale): void {
        this.animationHelper.animateBackground({ fromScale, toScale, duration, isEdpBackground: this.isEdpModalBackground }).subscribe(() => {
            this.renderer.removeStyle(this.backgroundWindow, this.willChange);
            this.renderer.removeStyle(this.backgroundWindow, 'transform');
            this.renderer.removeStyle(this.modalWindow, 'height');
            this.renderer.removeStyle(this.modalWindow, this.willChange);
            this.renderer.removeAttribute(this.modalWindow, 'style');

            if (toScale === this.animationHelper.maxBackgroundScale) {
                if (this.openedModals.count === 1 && this.openedModals.edpModal) {
                    this.renderer.removeClass(this.edpCoverElement, 'modal-gestures-edp-cover');
                    this.renderer.setStyle(this.edpCoverElement, 'height', `${this.coverElementHeight}px`);
                    this.renderer.addClass(this.edpCoverElement, 'modal-gestures-cover');
                }
                this.panClose();
            }
        });
    }

    private animateModal(shouldSlideOut: boolean, currentY: number, duration: number): void {
        this.animationHelper.animateModal(shouldSlideOut, { currentY }, duration).subscribe(() => {
            this.renderer.removeAttribute(this.modalWindow, 'style');
            this.animationHelper.tryShowSlotApp();
            this.isAnimating = false;
        });
    }

    private panClose(): void {
        this.renderer.removeAttribute(this.backgroundWindow, 'style');
        this.openedModals.activeModal?.close({ closeAction: PopupAction.CloseByPan });
    }

    private modifyModalHeight(): void {
        this.modalWindow.addEventListener('animationstart', () => this.renderer.setStyle(this.modalWindow, 'height', '100%'));

        if (this.slotApp) {
            this.mutationObserverService
                .observe(this.slotApp, { attributes: true, attributeFilter: ['style'] })
                .pipe(
                    filter((mutations) => mutations.some((mutation) => !(mutation.target as HTMLElement).getAttribute('style')?.includes('hidden'))),
                    takeUntilDestroyed(this.destroyRef),
                )
                .subscribe(() => {
                    this.renderer.removeStyle(this.modalWindow, 'height');
                });
        }
    }
}
